import { useState, useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
import { createClient, signIn as authSignIn, signUp as authSignUp, signOut as authSignOut, signInWithGoogle as authSignInWithGoogle } from '@lingaai/supabase-client';

// NOTE: useCurrentUser has been moved to '@lingaai/user-management'
// Import it from there instead: import { useCurrentUser } from '@lingaai/user-management';

// Custom hook for session management
export function useSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsLoading(false);
    });

    // Subscribe to session changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return { data: session, isLoading };
}

// Sign in hook - returns async function
export function useSignIn() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async ({ email, password }: { email: string; password: string }) => {
    setIsPending(true);
    setError(null);
    try {
      const result = await authSignIn(email, password);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Sign in failed');
      setError(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending, error };
}

// Sign up hook
export function useSignUp() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (params: { email: string; password: string; firstName: string; lastName: string; organization: string }) => {
    setIsPending(true);
    setError(null);
    try {
      const result = await authSignUp(params);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Sign up failed');
      setError(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending, error };
}

// Sign out hook
export function useSignOut() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async () => {
    setIsPending(true);
    setError(null);
    try {
      await authSignOut();
      // AuthStore will be cleared automatically via onAuthStateChange listener
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Sign out failed');
      setError(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending, error };
}

// Sign in with Google hook
export function useSignInWithGoogle() {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async () => {
    setIsPending(true);
    setError(null);
    try {
      const result = await authSignInWithGoogle();
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Google sign in failed');
      setError(error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending, error };
}
